import AppContext from 'App/AppContext';
import { IPSearchBox, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import QuickFilter, { QuickFilterContext } from 'components/Filter/QuickFilter';
import { filterIntegrations } from 'components/Integrations/FilterIntegrationsHelper';
import { globalFilterDelay, globalMinFilterChars } from 'globalConstants';
import { globalSearchBoxStyles, globalStackTokensGapMedium } from 'globalStyles';
import { Integration } from 'models/integrations/integration';
import { IntegrationTypeHandler, getIntegrationTypeHandlersForDisplay } from 'models/integrations/integrationTypeHandler';
import Task from 'models/tasks/task';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalStorageKeys, getLocalStorageData, setLocalStorageData } from 'utils/localstorage';
import { logTaskViewDiagnostic } from 'utils/taskViewDiagnostics';
import { FilterTaskGroupKeys } from './FilterTaskGroupKeys';
import FilterTasks, { QuickFilterTasksRenderer } from './FilterTasks';
import { filterTasks } from './FilterTasksHelper';
import { TaskViewMemberFilter } from './TaskViewMemberFilter';

interface ITaskViewFiltersProps {
  selectedFilter: string[];
  isLoading?: boolean;
  tasks: Task[];
  integrations?: Integration[];
  selectedTasks: Task[];
  selectedIntegrations?: Integration[];
  showGroupMemberFilter?: boolean;
  memberFilter?: string[];
  memberFilterViewKey?: string;
  lockedFilterValues?: string[];
  isLoadingMembership?: boolean;
  showFilterDialog?: boolean;
  filterStorageKey: LocalStorageKeys;
  hideQuickFilter?: boolean;
  quickFilterContext?: QuickFilterContext;
  maxQuickFilters?: number;
  disableLocalFilterStorage?: boolean;
  initialFilter?: string[] | undefined;
  onUpdateSelectedFilter: (filters: string[]) => void;
  onUpdateTasksDisplay: (newTasks: Task[]) => void;
  onUpdateIntegrationsDisplay: (newIntegrations: Integration[]) => void;
  onUpdateMemberFilter: (filter: string[]) => void;
  onLoadMembership: () => void;
  toggleFilterDialog?: () => void; //if this is not set, filter is not shown
}

export const TaskViewFilters = (props: ITaskViewFiltersProps) => {
  const { t } = useTranslation(['translation', 'tasks', 'adminGroups']);
  const appContext = useContext(AppContext);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [filterCountTasks, setFilterCountTasks] = useState<number>(0);
  const [filterCountIntegrations, setFilterCountIntegrations] = useState<number>(0);
  const [integrationTypeHandlers] = useState<IntegrationTypeHandler[]>(() => getIntegrationTypeHandlersForDisplay(t));
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const clearSearchTimer = useCallback(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = undefined;
    }
  }, []);

  //
  // Effects
  //
  useEffect(() => {
    return clearSearchTimer;
  }, [clearSearchTimer]);

  useEffect(() => {
    loadFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialFilter]);

  useEffect(() => {
    if (isInitializing) return;

    if (!props.disableLocalFilterStorage) {
      saveFilterToStorage(props.selectedFilter);
    }

    //Filter tasks
    let newTasks: Task[] = [...props.tasks];
    if (props.selectedFilter && props.selectedFilter.length > 0) {
      newTasks = filterTasks(props.selectedFilter, newTasks, appContext);
    }
    if (searchText && searchText.length >= globalMinFilterChars) {
      newTasks = applyTextFilter(newTasks, searchText);
    }

    if (props.selectedFilter?.length || props.initialFilter?.length) {
      // Lazy payload: the multi-pass task scans below only run when
      // diagnostics are actually enabled (production gates them off).
      logTaskViewDiagnostic('tasks.filters.apply', () => {
        const templateFilterIds: number[] = [];
        props.selectedFilter?.forEach((filter) => {
          if (!filter.startsWith(FilterTaskGroupKeys.template)) return;
          const id = Number(filter.substring(FilterTaskGroupKeys.template.length));
          if (!isNaN(id)) {
            templateFilterIds.push(id);
          }
        });

        const templateFilterIdSet = new Set(templateFilterIds);
        const sampleTemplateIds: number[] = [];
        const sampleTemplateIdSet = new Set<number>();
        let templateMatchCount = 0;
        let sameTaskIdCount = 0;
        let sameTaskMasterIdCount = 0;
        let tasksWithTemplateIdCount = 0;
        props.tasks.forEach((task) => {
          if (task.templateId !== undefined) {
            tasksWithTemplateIdCount += 1;
            if (!sampleTemplateIdSet.has(task.templateId) && sampleTemplateIds.length < 25) {
              sampleTemplateIdSet.add(task.templateId);
              sampleTemplateIds.push(task.templateId);
            }
            if (templateFilterIdSet.has(task.templateId)) {
              templateMatchCount += 1;
            }
          }
          if (templateFilterIdSet.has(task.taskId)) {
            sameTaskIdCount += 1;
          }
          if (task.taskMasterId !== undefined && templateFilterIdSet.has(task.taskMasterId)) {
            sameTaskMasterIdCount += 1;
          }
        });

        return {
          selectedFilter: props.selectedFilter,
          initialFilter: props.initialFilter,
          disableLocalFilterStorage: props.disableLocalFilterStorage,
          taskCountBefore: props.tasks.length,
          taskCountAfter: newTasks.length,
          hasSearchText: Boolean(searchText && searchText.length >= globalMinFilterChars),
          templateFilterIds,
          templateMatchCount: templateFilterIds.length ? templateMatchCount : undefined,
          sameTaskIdCount: templateFilterIds.length ? sameTaskIdCount : undefined,
          sameTaskMasterIdCount: templateFilterIds.length ? sameTaskMasterIdCount : undefined,
          tasksWithTemplateIdCount,
          sampleTemplateIds,
        };
      });
    }

    updateTasksDisplay(newTasks);

    //Filter integrations
    if (props.integrations) {
      let newIntegrations = [...props.integrations];
      if (props.selectedFilter && props.selectedFilter.length > 0) {
        newIntegrations = filterIntegrations(props.selectedFilter, newIntegrations, appContext);
      }
      if (searchText && searchText.length >= globalMinFilterChars) {
        newIntegrations = applyTextFilterIntegrations(newIntegrations, searchText);
      }

      updateIntegrationsDisplay(newIntegrations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedFilter, props.tasks, props.integrations, searchText]);

  const updateTasksDisplay = (tasks: Task[]) => {
    setFilterCountTasks(tasks.length);
    props.onUpdateTasksDisplay(tasks);
  };

  const updateIntegrationsDisplay = (integrations: Integration[]) => {
    setFilterCountIntegrations(integrations.length);
    props.onUpdateIntegrationsDisplay(integrations);
  };

  const updateSelectedFilter = (filter: string[]) => {
    props.onUpdateSelectedFilter(filter);
  };

  const saveFilterToStorage = (filter: string[]) => {
    setLocalStorageData(appContext, props.filterStorageKey, filter.join(','));
  };

  const loadFilter = async () => {
    //Always signal to parent that the filter is loaded by calling onUpdateSelectedFilter
    if (props.disableLocalFilterStorage) {
      // Parent-managed views (TaskViews) pass selected filters directly and should bypass local storage.
      setIsInitializing(false);

      return;
    }

    if (props.initialFilter) {
      //an initial filter is set, so we can use that, probably from URL
      props.onUpdateSelectedFilter(props.initialFilter);
    } else {
      const filterString = getLocalStorageData(appContext, props.filterStorageKey);
      if (filterString) {
        const filter = filterString.split(',');
        props.onUpdateSelectedFilter(filter);
      } else {
        props.onUpdateSelectedFilter([]);
      }
    }

    setIsInitializing(false);
  };

  const applyTextFilter = (tasks: Task[], text: string): Task[] => {
    const searchLower = text.toLowerCase();

    return tasks.filter(
      (item: Task) =>
        item.name.toLowerCase().includes(searchLower) ||
        (item.description && item.description.toLowerCase().includes(searchLower)) ||
        (item.checkList &&
          item.checkList.items.some(
            (c) =>
              c.description?.toLowerCase()?.includes(searchLower) || c.comment?.toLowerCase()?.includes(searchLower),
          )) ||
        item.taskId.toString() === searchLower,
    );
  };

  const applyTextFilterIntegrations = (integrations: Integration[], text: string): Integration[] => {
    const searchLower = text.toLowerCase();

    return integrations.filter((item: Integration) => {
      const integrationTypeHandler = integrationTypeHandlers.find(
        (h) => h.integrationTypeId === item.integrationTypeId,
      );
      if (integrationTypeHandler) {
        return (
          integrationTypeHandler.name?.toLowerCase()?.includes(searchLower) ||
          integrationTypeHandler.description?.toLowerCase()?.includes(searchLower)
        );
      }

      return false;
    });
  };

  const setQuickFilter = (quickFilter: string, checked: boolean) => {
    let newFilter = [...(props.selectedFilter ?? [])];
    if (!checked) {
      newFilter = newFilter.filter((f) => f !== quickFilter);
    } else {
      newFilter.push(quickFilter);
    }

    updateSelectedFilter(newFilter);
  };

  //
  // Main render
  //
  if (appContext.isMobileView) {
    return null; //no filters on mobile
  }

  return (
    <IPStack horizontal wrap verticalAlign="center" tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPSearchBox
          autoFocus
          underlined={true}
          styles={globalSearchBoxStyles}
          placeholder={t('translation:General.Filter.Placeholder')}
          onChange={(ev, newValue) => {
            clearSearchTimer();
            searchTimerRef.current = setTimeout(() => setSearchText(newValue), globalFilterDelay);
          }}
        />
      </IPStack.Item>
      {props.showGroupMemberFilter && (
        <TaskViewMemberFilter
          isLoadingMembership={props.isLoadingMembership}
          onUpdateFilter={props.onUpdateMemberFilter}
          filter={props.memberFilter ?? []}
          memberFilterViewKey={props.memberFilterViewKey}
          onLoadMembership={props.onLoadMembership}
        />
      )}
      {props.toggleFilterDialog && (
        <FilterTasks
          toggleDialog={props.toggleFilterDialog}
          isOpen={props.showFilterDialog ?? false}
          updateSelectedFilter={props.onUpdateSelectedFilter}
          selectedFilter={props.selectedFilter}
          lockedFilterValues={props.lockedFilterValues}
          hideClear={!props.hideQuickFilter}
          showWorkableTypeFilter={true}
          showTemplateFilter={true}
        />
      )}
      {!props.hideQuickFilter && (
        <IPStack.Item>
          <QuickFilter
            context={props.quickFilterContext ?? QuickFilterContext.generic}
            filters={props.selectedFilter ?? []}
            lockedFilterValues={props.lockedFilterValues}
            onChange={setQuickFilter}
            onFilterRender={QuickFilterTasksRenderer}
            enabled={!props.isLoading}
            maxFilters={props.maxQuickFilters}
          />
        </IPStack.Item>
      )}
      {!props.isLoading && (
        <IPStack.Item>
          <IPText variant="small">
            {t('translation:General.List.FilterCountWithSelection', {
              total: props.tasks.length,
              filtered: filterCountTasks + filterCountIntegrations,
              selected: props.selectedTasks.length + (props.selectedIntegrations?.length ?? 0),
            })}
          </IPText>
        </IPStack.Item>
      )}
    </IPStack>
  );
};
